import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, PropsWithChildren } from 'react'

import {
  FeedbackContext,
  type FeedbackScope,
  type FeedbackTone,
  type ShowFeedbackOptions,
} from '@/components/feedback/feedback-context'
import { cn } from '@/lib/cn'

interface FeedbackMessage {
  detail?: string
  id: string
  message: string
  scope: FeedbackScope
  title?: string
  tone: FeedbackTone
}

const defaultFeedbackDuration: Record<FeedbackTone, number | null> = {
  error: null,
  info: 3000,
  success: 2400,
  warning: 4200,
}

function createFeedbackId() {
  return crypto.randomUUID()
}

export function FeedbackProvider({ children }: PropsWithChildren) {
  const [messages, setMessages] = useState<Record<FeedbackScope, FeedbackMessage | null>>({
    drawer: null,
    global: null,
  })
  const timersRef = useRef<Record<FeedbackScope, number | null>>({
    drawer: null,
    global: null,
  })

  const clearTimer = useCallback((scope: FeedbackScope) => {
    const timer = timersRef.current[scope]
    if (timer !== null) {
      window.clearTimeout(timer)
      timersRef.current[scope] = null
    }
  }, [])

  const dismissFeedback = useCallback(
    (scope: FeedbackScope) => {
      clearTimer(scope)
      setMessages((current) => ({
        ...current,
        [scope]: null,
      }))
    },
    [clearTimer],
  )

  const showFeedback = useCallback(
    ({ detail, durationMs, message, scope = 'global', title, tone }: ShowFeedbackOptions) => {
      const normalizedMessage = message.trim()
      const normalizedDetail = detail?.trim()
      const normalizedTitle = title?.trim()
      if (!normalizedMessage && !normalizedTitle) {
        return
      }

      clearTimer(scope)
      setMessages((current) => ({
        ...current,
        [scope]: {
          id: createFeedbackId(),
          detail: normalizedDetail,
          message: normalizedMessage,
          scope,
          title: normalizedTitle,
          tone,
        },
      }))

      const resolvedDuration = durationMs === undefined ? defaultFeedbackDuration[tone] : durationMs
      if (resolvedDuration !== null && resolvedDuration > 0) {
        timersRef.current[scope] = window.setTimeout(() => {
          dismissFeedback(scope)
        }, resolvedDuration)
      }
    },
    [clearTimer, dismissFeedback],
  )

  useEffect(
    () => () => {
      clearTimer('drawer')
      clearTimer('global')
    },
    [clearTimer],
  )

  const contextValue = useMemo(
    () => ({
      dismissFeedback,
      showFeedback,
    }),
    [dismissFeedback, showFeedback],
  )

  return (
    <FeedbackContext.Provider value={contextValue}>
      {children}
      <FeedbackViewport messages={messages} onDismiss={dismissFeedback} />
    </FeedbackContext.Provider>
  )
}

function FeedbackViewport({
  messages,
  onDismiss,
}: {
  messages: Record<FeedbackScope, FeedbackMessage | null>
  onDismiss: (scope: FeedbackScope) => void
}) {
  return (
    <>
      <div className="pointer-events-none fixed top-5 left-1/2 z-[120] flex -translate-x-1/2 flex-col items-center gap-3">
        {messages.global ? (
          <FeedbackCard message={messages.global} onDismiss={() => onDismiss('global')} />
        ) : null}
      </div>
      <div className="pointer-events-none fixed top-20 right-6 z-[120] flex w-[360px] max-w-[calc(100vw-32px)] flex-col items-end gap-3 max-sm:right-4 max-sm:left-4 max-sm:w-auto">
        {messages.drawer ? (
          <FeedbackCard message={messages.drawer} onDismiss={() => onDismiss('drawer')} />
        ) : null}
      </div>
    </>
  )
}

function FeedbackCard({ message, onDismiss }: { message: FeedbackMessage; onDismiss: () => void }) {
  const isAssertive = message.tone === 'error' || message.tone === 'warning'
  const isError = message.tone === 'error'
  const isInfo = message.tone === 'info'
  const isSuccess = message.tone === 'success'
  const isWarning = message.tone === 'warning'
  const toneOverlayStyle: CSSProperties | undefined = isError
    ? {
        background:
          'linear-gradient(92deg, rgba(240, 68, 56, 0.25) 0%, rgba(255, 255, 255, 0) 100%)',
      }
    : isSuccess
      ? {
          background:
            'linear-gradient(92deg, rgba(23, 178, 106, 0.25) 0%, rgba(255, 255, 255, 0) 100%)',
        }
      : isWarning
        ? {
            background:
              'linear-gradient(92deg, rgba(247, 144, 9, 0.25) 0%, rgba(255, 255, 255, 0) 100%)',
          }
        : isInfo
          ? {
              background:
                'linear-gradient(92deg, rgba(11, 165, 236, 0.25) 0%, rgba(255, 255, 255, 0) 100%)',
            }
          : undefined
  const title = message.title ?? message.message
  const detail = message.title
    ? [message.message, message.detail].filter(Boolean).join('\n')
    : message.detail

  return (
    <div
      aria-live={isAssertive ? 'assertive' : 'polite'}
      className={cn(
        'feedback-message pointer-events-auto max-w-full rounded-xl',
        message.scope === 'global' ? 'min-w-[266px]' : 'w-full',
      )}
      data-state="open"
      role={isAssertive ? 'alert' : 'status'}
    >
      <div className="relative overflow-hidden rounded-xl border border-[var(--color-panel-border)] bg-white shadow-[0px_4px_12px_rgba(17,24,39,0.08),0px_12px_32px_rgba(17,24,39,0.08)]">
        {toneOverlayStyle ? (
          <div aria-hidden className="absolute -inset-px opacity-40" style={toneOverlayStyle} />
        ) : null}
        <div className="relative grid grid-cols-[20px_minmax(0,1fr)_20px] gap-x-3 overflow-hidden p-4">
          <div className="flex h-6 shrink-0 items-center justify-center">
            <FeedbackToneIcon tone={message.tone} />
          </div>
          <div className="min-w-0">
            <p className="break-words text-sm leading-6 font-semibold text-[#101828]">{title}</p>
            {detail ? (
              <p className="mt-1 max-h-28 overflow-auto whitespace-pre-wrap break-words text-xs leading-5 text-[var(--color-text-muted)]">
                {detail}
              </p>
            ) : null}
          </div>
          <button
            aria-label="关闭消息"
            className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-md text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-hover-muted)] hover:text-[var(--color-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
            onClick={onDismiss}
            type="button"
          >
            <span aria-hidden className="text-base leading-none">
              ×
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}

function FeedbackToneIcon({ tone }: { tone: FeedbackTone }) {
  if (tone === 'error') {
    return (
      <svg
        aria-hidden
        className="size-5 text-[var(--color-danger)]"
        fill="none"
        viewBox="0 0 20 20"
      >
        <path
          d="M10 18.333A8.333 8.333 0 1 0 10 1.667a8.333 8.333 0 0 0 0 16.666Z"
          fill="currentColor"
        />
        <path
          d="M10 5.625c.414 0 .75.336.75.75v4.25a.75.75 0 0 1-1.5 0v-4.25c0-.414.336-.75.75-.75ZM10 14.583a.958.958 0 1 0 0-1.916.958.958 0 0 0 0 1.916Z"
          fill="#fff"
        />
      </svg>
    )
  }

  if (tone === 'warning') {
    return (
      <svg aria-hidden className="size-5 text-[#f79009]" fill="none" viewBox="0 0 20 20">
        <path
          d="M8.548 2.858a1.667 1.667 0 0 1 2.904 0l7.095 12.557a1.667 1.667 0 0 1-1.452 2.486H2.905a1.667 1.667 0 0 1-1.452-2.486L8.548 2.858Z"
          fill="currentColor"
        />
        <path
          d="M10 7.083c.414 0 .75.336.75.75v3.334a.75.75 0 0 1-1.5 0V7.833c0-.414.336-.75.75-.75ZM10 14.792a.833.833 0 1 0 0-1.667.833.833 0 0 0 0 1.667Z"
          fill="#fff"
        />
      </svg>
    )
  }

  if (tone === 'success') {
    return (
      <svg aria-hidden className="size-5 text-[#17b26a]" fill="none" viewBox="0 0 20 20">
        <path
          d="M10 18.333A8.333 8.333 0 1 0 10 1.667a8.333 8.333 0 0 0 0 16.666Z"
          fill="currentColor"
        />
        <path
          d="m6.667 10.208 2.083 2.084 4.583-4.584"
          stroke="#fff"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.6"
        />
      </svg>
    )
  }

  return (
    <svg aria-hidden className="size-5 text-[#0ba5ec]" fill="none" viewBox="0 0 20 20">
      <path
        d="M10 18.333A8.333 8.333 0 1 0 10 1.667a8.333 8.333 0 0 0 0 16.666Z"
        fill="currentColor"
      />
      <path
        d="M10 9.167v4.583M10 6.25h.008"
        stroke="#fff"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  )
}
